import React, { useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../../../firebase/AuthProvider";
import { SaveHolidayAsync } from "../../../../services/HolidaysService/HolidaysService";
import { HolidayUpsertRequest } from "../../../../interfaces";
import { AxiosError } from "axios";

interface AddHolidayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddHolidayModal: React.FC<AddHolidayModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { currentUser } = useAuth();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // ---------- Helpers ----------

  const todayDateString = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`; // YYYY-MM-DD
  };

  const formatLocalDateTime = (dateStr: string, endOfDay = false) => {
    const d = new Date(dateStr);
    if (endOfDay) d.setHours(23, 59, 59, 999);
    else d.setHours(0, 0, 0, 0);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    const ms = String(d.getMilliseconds()).padStart(3, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}.${ms}`; // local datetime, no 'Z'
  };

  const reset = () => {
    setTitle("");
    setDescription("");
    setStartDate("");
    setEndDate("");
    setErrors({});
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    const today = todayDateString();

    if (!title.trim()) e.title = "Title is required";
    if (!startDate) e.startDate = "Start date is required";
    if (!endDate) e.endDate = "End date is required";

    if (startDate && startDate < today) {
      e.startDate = "Start date cannot be before today";
    }

    if (endDate && endDate < today) {
      e.endDate = "End date cannot be before today";
    }

    if (startDate && endDate && startDate > endDate) {
      e.endDate = "End date must be after or equal to start date";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ---------- Submit ----------

  const handleSubmit = async (ev?: React.FormEvent) => {
    ev?.preventDefault();
    if (!validate()) return;

    const token = await currentUser?.getIdToken();
    if (!token) {
      toast.error("Authentication error — please log in again.");
      return;
    }

    setLoading(true);
    try {
      const payload: HolidayUpsertRequest = {
        title: title.trim(),
        description: description.trim(),
        startDate: formatLocalDateTime(startDate), // 00:00:00
        endDate: formatLocalDateTime(endDate, true),       // 23:59:59.999
      };
      const res = await SaveHolidayAsync(token, payload);

      if (res.success) {
        toast.success("Holiday created successfully!");
        onSuccess?.();
        reset();
        onClose();
      } else {
        toast.error(res.message || "Failed to create holiday");
      }
    } catch (err: any) {
      toast.error(
        err instanceof AxiosError
          ? err.response?.data?.message
          : err?.message || "Error creating holiday"
      );
    } finally {
      setLoading(false);
    }
  };

  const todayStr = todayDateString();

  // ---------- UI ----------

  return (
    <Modal show={isOpen} onHide={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Holiday</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3 mt-4" controlId="holidayTitle">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title"
              isInvalid={!!errors.title}
            />
            <Form.Control.Feedback type="invalid">
              {errors.title}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="holidayDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
            />
          </Form.Group>

          <div className="row">
            <div className="col">
              <Form.Group className="mb-3" controlId="holidayStart">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  min={todayStr}
                  onChange={(e) => {
                    const val = e.target.value;
                    setStartDate(val);
                    setErrors((prev) => {
                      const copy = { ...prev };
                      delete copy.startDate;
                      delete copy.endDate;
                      return copy;
                    });
                    if (endDate && val > endDate) {
                      setEndDate(val);
                    }
                  }}
                  isInvalid={!!errors.startDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.startDate}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className="col">
              <Form.Group className="mb-3" controlId="holidayEnd">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  min={startDate || todayStr}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEndDate(val);
                    setErrors((prev) => {
                      const copy = { ...prev };
                      delete copy.endDate;
                      return copy;
                    });
                  }}
                  isInvalid={!!errors.endDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.endDate}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Save Holiday"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddHolidayModal;
